import { useEffect } from 'react';

interface EduAISEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article';
}

/**
 * SEO component for EduAI Solutions website
 * Optimized for Slovak market with B2B focus on schools, municipalities, and businesses
 */
export function EduAISEOHead({
  title,
  description,
  image = '/og-image.png',
  type = 'website'
}: EduAISEOHeadProps) {
  const siteName = "Peter Tej | EduAI Solutions";
  const defaultTitle = "AI školenia pre organizácie a tímy | EduAI Solutions";
  const defaultDescription = "Praktické AI školenia – ChatGPT, Gemini, NotebookLM. Ušetrite až 80 % času na administratíve. Vyše 2 040 vyškolených ľudí na Slovensku.";

  const fullTitle = title || defaultTitle;
  const fullDescription = description || defaultDescription;

  const baseUrl = window.location.origin;
  const fullUrl = baseUrl + window.location.pathname;
  const absoluteImage = image.startsWith('/') ? baseUrl + image : image;
  
  // Slovak keywords for AI education and consulting
  const keywords = [
    "AI školenia",
    "umelá inteligencia pre organizácie",
    "AI vo vzdelávaní",
    "AI pre tímy",
    "AI konzultant Slovensko",
    "ChatGPT školenie",
    "školenie AI nástroje",
    "automatizácia administratívy",
    "digitálna transformácia",
    "AI workshop firmy",
    "Gemini školenie",
    "NotebookLM školenie",
    "Peter Tej",
    "EduAI Solutions"
  ].join(", ");

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', fullDescription);
    updateMetaTag('author', 'Peter Tej');
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', 'Slovak');
    updateMetaTag('geo.region', 'SK');
    updateMetaTag('geo.placename', 'Slovensko');
    
    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', fullDescription, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:image', absoluteImage, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', 'sk_SK', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', fullDescription);
    updateMetaTag('twitter:image', absoluteImage);

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    // Preconnect hints removed - Stripe and Calendly load on demand, not on initial page load

    // Add JSON-LD structured data
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLd);
    }
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "EduAI Solutions",
      "description": fullDescription,
      "founder": {
        "@type": "Person",
        "name": "Peter Tej",
        "jobTitle": "AI Expert, Lektor, Konzultant"
      },
      "areaServed": {
        "@type": "Country",
        "name": "Slovakia"
      },
      "serviceType": [
        "AI Training",
        "AI Consulting",
        "Digital Transformation",
        "Educational Technology"
      ],
      "telephone": "+421915917042",
      "email": "info@petertej.sk",
      "url": fullUrl,
      "priceRange": "€49 - €299"
    };
    
    jsonLd.textContent = JSON.stringify(structuredData);

  }, [fullTitle, fullDescription, fullUrl, absoluteImage, type, keywords]);

  return null;
}
